class ProductGroupsSection extends HTMLElement {
  constructor() {
    super();
    this.offset = 68;
    this.activeHash = '';
    this.ticking = false;
    this.navFixed = false;
    this.handleClick = this.handleClick.bind(this);
    this.requestUpdate = this.requestUpdate.bind(this);
    this.updateOffset = this.updateOffset.bind(this);
  }

  connectedCallback() {
    this.mode = this.dataset.navigationMode || 'filter';
    this.navSlot = this.querySelector('[data-product-groups-nav-slot]');
    this.nav = this.querySelector('[data-product-groups-nav]') || this.querySelector('.product-groups-nav');
    this.scroller = this.querySelector('.product-groups-nav__scroller');
    this.stickyCta = this.querySelector('[data-product-groups-sticky-cta]');
    this.groups = Array.from(this.querySelectorAll('.product-group[id]'));
    this.links = Array.from(this.querySelectorAll('.product-groups-nav__link[href^="#"]'));
    this.groupLinks = this.links.filter((link) => !link.classList.contains('product-groups-nav__link--all'));
    this.allLinks = Array.from(this.querySelectorAll('.product-groups-nav__link--all'));

    if (this.stickyCta) {
      document.documentElement.classList.add('has-product-groups-sticky-cta');
    }

    if (!this.nav) return;

    this.nav.addEventListener('click', this.handleClick);
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.updateOffset);
    this.updateOffset();
    this.updateStickyNav();

    if (this.mode === 'filter') {
      this.showAllGroups();
      this.setAllActive();
    }
  }

  disconnectedCallback() {
    document.documentElement.classList.remove('has-product-groups-sticky-cta');
    if (!this.nav) return;
    this.nav.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.updateOffset);
  }

  getPageTop(element) {
    return element.getBoundingClientRect().top + window.pageYOffset;
  }

  updateOffset() {
    const header =
      document.querySelector('.site-header--stuck') ||
      document.querySelector('.site-header') ||
      document.querySelector('.header-wrapper');
    if (header) {
      this.offset = Math.max(0, Math.ceil(header.getBoundingClientRect().bottom));
    } else {
      this.offset = 0;
    }
    this.style.setProperty('--product-groups-sticky-offset', `${this.offset}px`);
    this.updateStickyNav();
  }

  updateStickyNav() {
    if (!this.nav || !this.navSlot) return;

    const slotTop = this.navSlot.getBoundingClientRect().top;
    const shouldFix = slotTop <= this.offset;

    if (shouldFix) {
      if (!this.navFixed) {
        this.navSlot.style.height = `${this.nav.offsetHeight}px`;
        this.nav.classList.add('is-fixed');
        this.navFixed = true;
      }
      this.nav.style.top = `${this.offset}px`;
    } else if (this.navFixed) {
      this.nav.classList.remove('is-fixed');
      this.nav.style.top = '';
      this.navSlot.style.height = '';
      this.navFixed = false;
    }
  }

  centerActiveMobileLink() {
    if (!this.scroller) return;
    const activeLink = this.scroller.querySelector('.product-groups-nav__link--active');
    if (!activeLink) return;
    const scrollLeft =
      activeLink.offsetLeft - this.scroller.clientWidth / 2 + activeLink.offsetWidth / 2;
    this.scroller.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }

  clearActiveLinks() {
    this.links.forEach((link) => {
      link.classList.remove('product-groups-nav__link--active');
    });
  }

  setActive(nextHash) {
    if (this.activeHash === nextHash) return;
    this.activeHash = nextHash;
    this.clearActiveLinks();
    this.groupLinks.forEach((link) => {
      link.classList.toggle(
        'product-groups-nav__link--active',
        link.getAttribute('href') === nextHash
      );
    });
    this.centerActiveMobileLink();
  }

  setAllActive() {
    if (this.activeHash === 'all') return;
    this.activeHash = 'all';
    this.clearActiveLinks();
    this.allLinks.forEach((link) => {
      link.classList.add('product-groups-nav__link--active');
    });
    this.centerActiveMobileLink();
  }

  showAllGroups() {
    this.groups.forEach((group) => {
      group.hidden = false;
      group.classList.remove('is-hidden');
      group.removeAttribute('aria-hidden');
    });
  }

  showGroup(targetHash) {
    this.groups.forEach((group) => {
      const shouldHide = `#${group.id}` !== targetHash;
      group.hidden = shouldHide;
      group.classList.toggle('is-hidden', shouldHide);
      if (shouldHide) {
        group.setAttribute('aria-hidden', 'true');
      } else {
        group.removeAttribute('aria-hidden');
      }
    });
  }

  updateActiveLink() {
    if (this.mode !== 'anchor' || !this.groups.length) return;

    const navHeight = this.nav.offsetHeight;
    const activationPoint = window.pageYOffset + this.offset + navHeight + 24;
    const firstGroupTop = this.getPageTop(this.groups[0]);
    let activeGroup = null;

    this.groups.forEach((group) => {
      if (this.getPageTop(group) <= activationPoint) {
        activeGroup = group;
      }
    });

    if (!activeGroup && activationPoint < firstGroupTop) {
      this.setAllActive();
    } else if (activeGroup) {
      this.setActive(`#${activeGroup.id}`);
    }
  }

  requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.updateOffset();
      this.updateActiveLink();
      this.ticking = false;
    });
  }

  handleClick(event) {
    const clickedElement = event.target instanceof Element ? event.target : null;
    const link = clickedElement?.closest('.product-groups-nav__link[href^="#"]');
    if (!link || !this.nav.contains(link)) return;

    const targetHash = link.getAttribute('href');
    const target = document.querySelector(targetHash);
    if (!target) return;

    event.preventDefault();
    this.updateOffset();

    const isAllLink = link.classList.contains('product-groups-nav__link--all');
    if (isAllLink) {
      this.setAllActive();
    } else {
      this.setActive(targetHash);
    }

    if (this.mode === 'filter') {
      if (isAllLink) {
        this.showAllGroups();
      } else {
        this.showGroup(targetHash);
      }
      return;
    }

    window.scrollTo({
      top: this.getPageTop(target) - this.offset - this.nav.offsetHeight,
      behavior: 'smooth',
    });
  }
}

customElements.get('product-groups-section') ||
  customElements.define('product-groups-section', ProductGroupsSection);
